export const CHUNK_RELOAD_STORAGE_KEY = "__shelpuk_chunk_reload_attempted__";
export const CHUNK_RELOAD_QUERY_PARAM = "__chunk_reload";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type HistoryLike = Pick<History, "replaceState">;
type LocationLike = Pick<Location, "href" | "replace">;
// The only member of the event this module reads -- the handler below calls
// `event.preventDefault?.()` and nothing else. Declared as that contract
// rather than as `Event`: a real `Event` still satisfies it, while a test
// double no longer has to fabricate 21 DOM members to be accepted.
export type PreloadErrorEvent = { preventDefault?: () => void };

type Listener = (event: PreloadErrorEvent) => void;

type RecoveryWindow = {
  addEventListener: (type: string, listener: Listener) => void;
  location: LocationLike;
  sessionStorage?: StorageLike;
  history?: HistoryLike;
};

function toUrlOrNull(href: string): URL | null {
  try {
    return new URL(href);
  } catch {
    return null;
  }
}

function cleanupRecoveryMarker(target: RecoveryWindow) {
  const url = toUrlOrNull(target.location.href);
  if (!url || !url.searchParams.has(CHUNK_RELOAD_QUERY_PARAM)) {
    return;
  }

  url.searchParams.delete(CHUNK_RELOAD_QUERY_PARAM);
  target.history?.replaceState(null, "", url.toString());
  safeStorageRemove(target.sessionStorage, CHUNK_RELOAD_STORAGE_KEY);
}

function buildRecoveryUrl(href: string, now: number): string {
  const url = toUrlOrNull(href);
  if (!url) {
    return href;
  }

  url.searchParams.set(CHUNK_RELOAD_QUERY_PARAM, String(now));
  return url.toString();
}

function safeStorageGet(storage: StorageLike | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeStorageSet(storage: StorageLike | undefined, key: string, value: string) {
  try {
    storage?.setItem(key, value);
  } catch {
    // Ignore storage access errors (private mode or restricted environments).
  }
}

function safeStorageRemove(storage: StorageLike | undefined, key: string) {
  try {
    storage?.removeItem(key);
  } catch {
    // Ignore storage access errors (private mode or restricted environments).
  }
}

export function installVitePreloadErrorRecovery(
  target: RecoveryWindow = window,
  now: () => number = () => Date.now(),
  // Vite fires one identity-less event for every failed wrapped dynamic
  // import, including the optional background locale preloads whose failures
  // the i18n loader handles itself ("logged and skipped"). The caller supplies
  // this predicate so such benign failures do not escalate to a reload -- a
  // reload here lands on the pre-commit URL and cancels an in-flight lazy
  // navigation. Suppressed events set no latch, so a later genuine route
  // failure still recovers.
  shouldSkipRecovery: () => boolean = () => false,
) {
  cleanupRecoveryMarker(target);

  target.addEventListener("vite:preloadError", (event) => {
    event.preventDefault?.();

    if (shouldSkipRecovery()) {
      return;
    }

    if (safeStorageGet(target.sessionStorage, CHUNK_RELOAD_STORAGE_KEY) === "1") {
      return;
    }

    safeStorageSet(target.sessionStorage, CHUNK_RELOAD_STORAGE_KEY, "1");
    const reloadUrl = buildRecoveryUrl(target.location.href, now());
    target.location.replace(reloadUrl);
  });
}
